import { Mesh as BABYLON_Mesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.js';
import { VertexData as BABYLON_VertexData } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.vertexData.js';
import { LineType, IVector3, IBoundingBox } from '../api.js';
import { Engine as BABYLON_Engine } from '@inter-ikea-betrakta-babylonjs/core/Engines/engine.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { PBRMaterial as BABYLON_PBRMaterial } from '@inter-ikea-betrakta-babylonjs/core/Materials/PBR/pbrMaterial.js';
import { RenderingGroupID } from './Viewer.js';
import { RenderLayerMask } from './RenderLayer.js';
// The number of array elements we need to store vertecies, colour and indecies.
const NUMBER_OF_SIDES = 8;
const POSITIONS_PER_LINE = 2 * NUMBER_OF_SIDES * 3 + 2 * 3; // (2 vertices per side + 2 cap vertices) * 3 floats per vertex.
const COLORS_PER_LINE = 2 * NUMBER_OF_SIDES * 4 + 2 * 4; // - || - but 4 floats per vertex color.
const INDICES_PER_LINE = (2 * NUMBER_OF_SIDES + 2 * NUMBER_OF_SIDES) * 3; // 2 triangles for the sides, 2 triangles for each end of the caps, 3 indices per triangle
/**
 * The line manager handles the creation and updating of the line mesh. The line mesh consists
 * of all lines in the scene (one drawcall). Lines are created as cylinders and can be either solid (one cylinder/sublineline)
 * or dashed (several cylinders/sublines).
 */
// NOTE: The LineManager only ever expands the size of the internal storages for the mesh containing all lines.
// So creating a ton of lines and then removing them would result in a lot of unused memory being blocked.
// This is unlikely (as we don't expect users to ever have large number of lines) to be a practical issue but still important to be aware of.
export class LineManager {
    constructor(_viewer, render_layer, draw_on_top) {
        this._viewer = _viewer;
        this.render_layer = render_layer;
        this.draw_on_top = draw_on_top;
        this._dirty = false;
        this._number_of_lines_buffered = 0;
        this._line_id_to_line = new Map(); // This solution relies on insertion order. Line ids will increase as the map grows.
        this._line_to_subline_count = new Map();
        this._cos_and_sin_angles = new Array(); // The lines are built by using cylinders, defined by cos and sin theta angles
        this._vertex_data = new BABYLON_VertexData();
        this._mesh = new BABYLON_Mesh('custom_lines', this._viewer.scene); // We use one mesh for all lines
        for (let i = 0; i < NUMBER_OF_SIDES; i++) {
            this._cos_and_sin_angles.push([Math.cos(2.0 * Math.PI * (i / NUMBER_OF_SIDES)), Math.sin(2.0 * Math.PI * (i / NUMBER_OF_SIDES))]);
        }
        // Create a unlit material, so that the lines appear 2D (without depth), even though we draw them in 3D.
        // Changed from StandardMaterial (emissive and disabled lighting) to use PBR, as most other objects are drawn using PBR.
        this._material = new BABYLON_PBRMaterial('lines_mat', this._viewer.scene);
        this._material.unlit = true;
        this._material.depthFunction = this.draw_on_top ? BABYLON_Engine.ALWAYS : BABYLON_Engine.LESS;
        this._mesh.layerMask = new RenderLayerMask([render_layer]).bitmap;
        this._mesh.material = this._material;
        this._mesh.isPickable = false;
        this._mesh.renderingGroupId = RenderingGroupID.DEFAULT_RENDERING_GROUP;
    }
    new_line_id() {
        return LineManager._line_id_counter++;
    }
    dirty() {
        this._dirty = true;
        // rebuild_if_dirty is called from the render loop, so we need to make sure
        // the scene is dirty as well so we actually render.
        this._viewer.mark_scene_dirty();
        this._viewer.mark_world_bounds_dirty();
    }
    static calculate_number_of_sub_lines(api_line) {
        if (api_line.type === LineType.SOLID) {
            return 1; // Solid line means one line
        }
        let sub_line_count = 0;
        const length = api_line.p0.subtract(api_line.p1).length();
        const dash_frequency = api_line.thickness * 4.0; // Magic number
        sub_line_count = Math.ceil(length / dash_frequency);
        if (sub_line_count % 2 === 0) {
            sub_line_count += 1;
        }
        return sub_line_count;
    }
    get_mesh() {
        return this._mesh;
    }
    rebuild_if_dirty() {
        if (!this._dirty) {
            return;
        }
        // We need to know how many lines we need to account for due to sublines when building our data arrays
        let number_of_lines = 0;
        this._line_to_subline_count.clear();
        for (const line of this._line_id_to_line.values()) {
            if (!line.is_visible) {
                continue;
            }
            const sub_line_count = line.is_visible ? LineManager.calculate_number_of_sub_lines(line) : 0;
            number_of_lines += sub_line_count;
            this._line_to_subline_count.set(line, sub_line_count);
        }
        // If we're not rendering any lines, and we don't have any "active" lines from before, we can (and need to) early out.
        if (number_of_lines === 0 && this._number_of_lines_buffered === 0) {
            return;
        }
        // Create new buffers if needed
        if (number_of_lines > this._number_of_lines_buffered) {
            this._vertex_data.positions = new Float32Array(number_of_lines * POSITIONS_PER_LINE);
            this._vertex_data.colors = new Float32Array(number_of_lines * COLORS_PER_LINE);
            this._vertex_data.indices = new Uint32Array(number_of_lines * INDICES_PER_LINE);
            this._number_of_lines_buffered = number_of_lines;
        }
        const { positions, colors, indices } = this._vertex_data;
        // VertexData props can be number[] | Float32Array | Uint32Array. Since initiation is optional, we need to verify that correct instances were created
        const vertex_data_initialized = positions instanceof Float32Array && colors instanceof Float32Array && indices instanceof Uint32Array;
        if (!vertex_data_initialized) {
            throw new Error('VertexData buffers are not correctly initialized');
        }
        // Fill index data with 0. We might have removed or hidden lines.
        indices.fill(0, 0);
        this.populate_buffers(positions, colors, indices);
        // Only send the subarray of used indices
        const used_indices = indices.subarray(0, number_of_lines * INDICES_PER_LINE);
        this._vertex_data.indices = used_indices;
        // Babylon will create new buffers for this. So the moment we make a change, we will create new buffers.
        this._vertex_data.applyToMesh(this._mesh); // positions, colors and indices are applied to mesh
        this._mesh.setEnabled(this._vertex_data.indices.length !== 0); // Disable mesh when it is empty to avoid gl errors.
        // Reset to the original index array
        this._vertex_data.indices = indices;
        this._dirty = false;
    }
    populate_buffers(positions, colors, indices) {
        // Set the start indecies of the buffers
        let current_position = 0;
        let current_color = 0;
        let current_index = 0;
        let line_index = 0;
        for (const line of this._line_id_to_line.values()) {
            if (!line.is_visible) {
                continue;
            }
            const p0p1 = line.p1.subtract(line.p0);
            const tangent = p0p1.normalizeToNew();
            const perp = IVector3.perpendicular(tangent);
            const normal = new BABYLON_Vector3(perp.x, perp.y, perp.z).normalize();
            const binormal = tangent.cross(normal);
            const radius = line.thickness * 0.5;
            const sub_lines_needed = this._line_to_subline_count.get(line);
            line.sub_lines = 0;
            for (let sub_line_index = 0; sub_line_index < sub_lines_needed; sub_line_index++) {
                if (sub_line_index % 2 === 0) {
                    const sub_line_p0 = BABYLON_Vector3.Lerp(line.p0, line.p1, sub_line_index / sub_lines_needed);
                    const sub_line_p1 = BABYLON_Vector3.Lerp(line.p0, line.p1, (sub_line_index + 1) / sub_lines_needed);
                    for (let i = 0; i < 2; i++) {
                        const lerped_position = BABYLON_Vector3.Lerp(sub_line_p0, sub_line_p1, i);
                        // Loop though all sides of the cylinder
                        for (let side = 0; side < NUMBER_OF_SIDES; side++) {
                            // We calculate the position offset of the vertex for this side
                            const cos_theta = this._cos_and_sin_angles[side][0];
                            const sin_theta = this._cos_and_sin_angles[side][1];
                            const offset = normal.scale(cos_theta).addInPlace(binormal.scale(sin_theta)).scaleInPlace(radius);
                            positions[current_position++] = lerped_position.x + offset.x;
                            positions[current_position++] = lerped_position.y + offset.y;
                            positions[current_position++] = lerped_position.z + offset.z;
                            colors[current_color++] = line.color.r;
                            colors[current_color++] = line.color.g;
                            colors[current_color++] = line.color.b;
                            colors[current_color++] = line.color.a;
                        }
                        positions[current_position++] = lerped_position.x;
                        positions[current_position++] = lerped_position.y;
                        positions[current_position++] = lerped_position.z;
                        colors[current_color++] = line.color.r;
                        colors[current_color++] = line.color.g;
                        colors[current_color++] = line.color.b;
                        colors[current_color++] = line.color.a;
                    }
                    const start_cap_index = line_index + NUMBER_OF_SIDES;
                    const end_cap_index = start_cap_index + NUMBER_OF_SIDES + 1;
                    // Push all the indices
                    const previous_loop_first_index = line_index;
                    const next_loop_first_index = line_index + NUMBER_OF_SIDES + 1;
                    for (let i = 0; i < NUMBER_OF_SIDES; i++) {
                        const top_left_index = previous_loop_first_index + i;
                        const bottom_left_index = previous_loop_first_index + (i + 1) % NUMBER_OF_SIDES;
                        const top_right_index = next_loop_first_index + i;
                        const bottom_right_index = next_loop_first_index + (i + 1) % NUMBER_OF_SIDES;
                        // Side quad
                        indices[current_index++] = bottom_left_index;
                        indices[current_index++] = top_left_index;
                        indices[current_index++] = top_right_index;
                        indices[current_index++] = top_right_index;
                        indices[current_index++] = bottom_right_index;
                        indices[current_index++] = bottom_left_index;
                        // start cap
                        indices[current_index++] = top_left_index;
                        indices[current_index++] = bottom_left_index;
                        indices[current_index++] = start_cap_index;
                        // end cap
                        indices[current_index++] = bottom_right_index;
                        indices[current_index++] = top_right_index;
                        indices[current_index++] = end_cap_index;
                    }
                    line_index = end_cap_index + 1;
                    line.sub_lines++;
                }
            }
        }
    }
    create_line(type, p0, p1, color, thickness) {
        const id = this.new_line_id();
        this._line_id_to_line.set(id, {
            sub_lines: 0,
            type: type,
            id: id,
            p0: p0,
            p1: p1,
            color: color.toLinearSpace(),
            thickness: thickness,
            is_visible: true
        });
        this.dirty();
        return id;
    }
    update_line(line_id, p0, p1, color, thickness) {
        let dirty = false;
        const line = this._line_id_to_line.get(line_id);
        if (line === undefined) {
            console.warn('Cannot update line. Line with line id', line_id, 'does not exist.');
            return;
        }
        if (p0 && !line.p0.equals(p0)) {
            line.p0 = p0;
            dirty = true;
        }
        if (p1 && !line.p1.equals(p1)) {
            line.p1 = p1;
            dirty = true;
        }
        if (color && !line.color.equals(color)) {
            line.color = color.toLinearSpace();
            dirty = true;
        }
        if (thickness !== undefined && line.thickness !== thickness) {
            line.thickness = thickness;
            dirty = true;
        }
        if (dirty) {
            this.dirty();
        }
    }
    get_bounds() {
        const lines = this._line_id_to_line.values();
        const bound = IBoundingBox.Invalid();
        for (const line of lines) {
            const line_bound = IBoundingBox.Invalid();
            IBoundingBox.encapsulate_in_place(line_bound, line.p0, line.p1);
            // To make things easy, we just increase the bounds size with the lines thickness.
            // Not so nice, but it works.
            line_bound.min.x -= line.thickness;
            line_bound.min.y -= line.thickness;
            line_bound.min.z -= line.thickness;
            line_bound.max.x += line.thickness;
            line_bound.max.y += line.thickness;
            line_bound.max.z += line.thickness;
            IBoundingBox.encapsulate_in_place(bound, line_bound.min, line_bound.max);
        }
        return bound;
    }
    set_visible(line_id, line_visible) {
        const line = this._line_id_to_line.get(line_id);
        if (line === undefined) {
            console.warn('Cannot set line visibility. Line with line id', line_id, 'does not exist.');
            return;
        }
        if (line.is_visible !== line_visible) {
            line.is_visible = line_visible;
            this.dirty();
        }
    }
    get_visible(line_id) {
        const line = this._line_id_to_line.get(line_id);
        return !!(line === null || line === void 0 ? void 0 : line.is_visible);
    }
    remove_line(line_id) {
        const line = this._line_id_to_line.get(line_id);
        if (line === undefined) {
            // Trying to delete a line that does not exist.
            return false;
        }
        this._line_id_to_line.delete(line_id);
        this.dirty();
        return true;
    }
    contains(line_id) {
        return this._line_id_to_line.has(line_id);
    }
}
LineManager._line_id_counter = 1;
//# sourceMappingURL=LineManager.js.map